// app.tsx
import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "@/context/AuthProvider";
import Layout from "./layout";
import { AuthGate } from "./layout/AuthGate";
import { OnboardingGate } from "./layout/OnboardingGate";
import { RouteFallback } from "./components/RouteProgress";
import { RunnerErrorBoundary } from "./pages/MockTest/attempt/[attemptId]/components/RunnerErrorBoundary";
import {
  HomeSkeleton,
  ListPageSkeleton,
  SplitPageSkeleton,
  CenteredSkeleton,
} from "./components/PageSkeleton";

const lazyLoad = (
  importFunc: () => Promise<{ default: React.ComponentType }>,
) => {
  return React.lazy(() =>
    importFunc().catch(() => ({
      default: () => <div>Failed to load component</div>,
    })),
  );
};

// Lazy loaded components
const NotFound = lazyLoad(() => import("./pages/404"));
const HomePage = lazyLoad(() => import("./pages/Home"));
const MockTest = lazyLoad(() => import("./pages/MockTest"));
const SubjectPapersPage = lazyLoad(
  () => import("./pages/MockTest/[subjectId]/index"),
);
const MyMockTestsPage = lazyLoad(
  () => import("./pages/MockTest/my-tests/index"),
);
const MockTestAttemptPage = lazyLoad(
  () => import("./pages/MockTest/attempt/[attemptId]/index"),
);
const MockTestResultsPage = lazyLoad(
  () => import("./pages/MockTest/attempt/[attemptId]/results/index"),
);
const OnboardingPage = lazyLoad(() => import("./pages/Onboarding/index"));
const SyllabusPage = lazyLoad(() => import("./pages/Syllabus/index"));
const AnnouncementsPage = lazyLoad(() => import("./pages/Announcements/index"));
const BookmarksPage = lazyLoad(() => import("./pages/bookmarks/index"));
const PracticePage = lazyLoad(() => import("./pages/Practice/index"));
const ProgressPage = lazyLoad(() => import("./pages/Progress/index"));
const StudyPage = lazyLoad(() => import("./pages/Study/index"));
const PricingPage = lazyLoad(() => import("./pages/Pricing/index"));
const SettingsPage = lazyLoad(() => import("./pages/Settings/index"));
const DailyPage = lazyLoad(() => import("./pages/Daily/index"));
const ContestsHubPage = lazyLoad(() => import("./pages/Contests/index"));
const CreateContestPage = lazyLoad(
  () => import("./pages/Contests/CreateContest"),
);
const JoinContestPage = lazyLoad(() => import("./pages/Contests/JoinLanding"));
const ContestAttemptPage = lazyLoad(
  () => import("./pages/Contests/ContestAttempt"),
);
const ContestLeaderboardPage = lazyLoad(
  () => import("./pages/Contests/ContestLeaderboard"),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Picks the skeleton that matches the route's real layout.
 *
 * A single centred spinner would make every navigation look identical and
 * then jump when content lands. Matching the page's actual shape means the
 * content settles into place instead.
 */
function RouteSuspense({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  const skeleton = (() => {
    if (pathname === "/") return <HomeSkeleton />;
    if (pathname.startsWith("/onboarding")) return <CenteredSkeleton />;
    if (
      pathname.startsWith("/syllabus") ||
      pathname.startsWith("/announcements") ||
      pathname.startsWith("/contests")
    ) {
      return <SplitPageSkeleton />;
    }
    return <ListPageSkeleton />;
  })();

  return (
    <Suspense fallback={<RouteFallback>{skeleton}</RouteFallback>}>
      {children}
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <ErrorBoundary>
              <AuthGate>
                <OnboardingGate>
                  <Layout>
                    <RouteSuspense>
                      <Routes>
                        <Route path="/" element={<HomePage />} />

                        <Route path="/mock-tests" element={<MockTest />} />
                        <Route
                          path="/mock-tests/:subjectId"
                          element={<SubjectPapersPage />}
                        />
                        <Route
                          path="/mock-tests/my-tests"
                          element={<MyMockTestsPage />}
                        />
                        {/* The runner gets its own boundary: a render error
                            during a live, timed paper must not white-screen a
                            student out of their attempt. */}
                        <Route
                          path="/mock-tests/attempt/:attemptId"
                          element={
                            <RunnerErrorBoundary>
                              <MockTestAttemptPage />
                            </RunnerErrorBoundary>
                          }
                        />
                        <Route
                          path="/mock-tests/attempt/:attemptId/results"
                          element={<MockTestResultsPage />}
                        />

                        <Route
                          path="/onboarding"
                          element={<OnboardingPage />}
                        />
                        <Route path="/syllabus" element={<SyllabusPage />} />
                        {/* Public, shareable: /syllabus/cs. The chooser is
                            the landing when no paper is named. */}
                        <Route
                          path="/syllabus/:subject"
                          element={<SyllabusPage />}
                        />
                        <Route
                          path="/announcements"
                          element={<AnnouncementsPage />}
                        />
                        <Route path="/bookmarks" element={<BookmarksPage />} />
                        <Route path="/practice" element={<PracticePage />} />
                        <Route path="/progress" element={<ProgressPage />} />
                        <Route path="/study" element={<StudyPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/daily" element={<DailyPage />} />

                        <Route path="/contests" element={<ContestsHubPage />} />
                        <Route
                          path="/contests/new"
                          element={<CreateContestPage />}
                        />
                        <Route
                          path="/contests/j/:token"
                          element={<JoinContestPage />}
                        />
                        <Route
                          path="/contests/:contestId/play"
                          element={<ContestAttemptPage />}
                        />
                        <Route
                          path="/contests/:contestId/results"
                          element={<ContestLeaderboardPage />}
                        />

                        {/* Retired. Both were unreachable from the new nav and
are kept only as redirects so existing bookmarks and
any shared links land somewhere sensible instead of a
404. */}
                        <Route
                          path="/study-planner"
                          element={<Navigate to="/" replace />}
                        />
                        <Route
                          path="/after-gate"
                          element={<Navigate to="/" replace />}
                        />

                        {/* 404 Route */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </RouteSuspense>
                  </Layout>
                </OnboardingGate>
              </AuthGate>
            </ErrorBoundary>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
